import {Anchor} from 'src/design/atoms/Anchor';
import {Avatar} from 'src/design/atoms/Avatar';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import React, {useMemo} from 'react';
import type {InspectionSimpleResponse} from 'src/api/ProactApiClient';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {GroupedList} from 'src/design/molecules/GroupedList';
import {isInspectionEditableState} from 'src/pages/Modules/Inspections/shared/inspectionHelpers';
import type {TimelineMode} from '../inspectionTimelineGrouping';
import {groupByTimeline} from '../inspectionTimelineGrouping';
import {InspectionCard} from './InspectionCard';

export interface InspectionTimelineListProps {
  items: InspectionSimpleResponse[];
  mode: TimelineMode;
  isLoading: boolean;
  canManage: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onView: (row: InspectionSimpleResponse) => void;
  onEdit: (row: InspectionSimpleResponse) => void;
  onDelete: (row: InspectionSimpleResponse) => void;
  onAssign: (row: InspectionSimpleResponse) => void;
}

const AssigneeSlot: React.FC<{
  row: InspectionSimpleResponse;
  canManage: boolean;
  onAssign: (row: InspectionSimpleResponse) => void;
}> = ({row, canManage, onAssign}) => {
  if (row.assigneeName) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Avatar size="sm" radius="xl" name={row.assigneeName} color="initials" />
        <Text size="sm">{row.assigneeName}</Text>
      </Group>
    );
  }

  if (canManage && row.state !== InspectionStateEnum.Completed) {
    return (
      <Anchor size="sm" fw={600} c="blue" onClick={() => onAssign(row)}>
        ● Unassigned · assign
      </Anchor>
    );
  }

  return (
    <Text size="sm" c="dimmed">
      Unassigned
    </Text>
  );
};

export const InspectionTimelineList: React.FC<InspectionTimelineListProps> = ({
  items,
  mode,
  isLoading,
  canManage,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
  onAssign,
}) => {
  const groups = useMemo(
    () => groupByTimeline(items, mode, row => row.scheduledStartAt),
    [items, mode]
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (groups.length === 0) {
    return <NoItemsPanel noItemsText="No inspections match these filters." />;
  }

  return (
    <GroupedList
      groups={groups}
      getItemKey={row => row.id}
      renderItem={row => {
        const editable = isInspectionEditableState(row.state);
        return (
          <InspectionCard
            inspection={row}
            isAssignedToMe={false}
            origin="manage"
            assignSlot={
              <AssigneeSlot row={row} canManage={canManage} onAssign={onAssign} />
            }
            actions={
              <TableRowActions>
                <TableRowActions.View onClick={() => onView(row)} />
                {canEdit && editable && (
                  <TableRowActions.Edit onClick={() => onEdit(row)} />
                )}
                {canDelete && editable && (
                  <TableRowActions.Menu>
                    <TableRowActions.MenuItem color="red" onClick={() => onDelete(row)}>
                      Delete
                    </TableRowActions.MenuItem>
                  </TableRowActions.Menu>
                )}
              </TableRowActions>
            }
          />
        );
      }}
    />
  );
};
